import { NavLink, useLocation } from "react-router-dom";
import { FileText, Briefcase, Sparkles, Map as MapIcon, ClipboardList, Building2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { trackNavClick } from "../../../lib/funnels";
import { loginPopupHandler } from "../../../redux/features/auth/loginSlice";

/**
 * Mobile bottom tab bar, the primary way to switch between the main
 * sections (Locker / Documents / Services / Intelligence) on small
 * screens, so users don't have to open the hamburger every time.
 *
 * Shown only below 1020px (matches the breakpoint the rest of the
 * mobile navbar uses). On desktop the center pill nav handles this.
 *
 * Active state is resolved by URL prefix, the same scheme the top
 * navbar uses (NAV_MATCHERS), because detail pages route to top-level
 * slugs (e.g. /telangana, /sale-deed) that don't sit under the section
 * path. Each tab stays lit while the user is anywhere inside its area.
 */
// Order mirrors the web header: Documents → Services → Intelligence → Maps
// (Maps sits next to Intelligence), then Orders.
const TABS = [
	{
		label: "Documents",
		path: "/states",
		match: "/states",
		navId: "property_docs",
		icon: FileText,
	},
	{
		label: "Services",
		path: "/services",
		match: "/services",
		navId: "property_services",
		icon: Briefcase,
	},
	// Hidden for now: Intelligence
	// {
	// label: "Intelligence",
	// path: "/intelligence",
	// match: "/intelligence",
	// navId: "intelligence",
	// icon: Sparkles,
	// isNew: true,
	// },
	{
		label: "Survey Maps",
		path: "/maps?tab=survey",
		// Scoped to /maps so it doesn't also light on Properties (everything
		// under /properties, its own tab below).
		match: "/maps",
		navId: "maps",
		icon: MapIcon,
	},
	{
		label: "Properties",
		path: "/properties/listings",
		match: "/properties",
		navId: "listings",
		icon: Building2,
	},
	{
		label: "Orders",
		path: "/customer/dashboard/overview",
		match: "/customer/dashboard",
		navId: "orders",
		icon: ClipboardList,
		requiresAuth: true,
	},
];

export default function BottomNav() {
	const { pathname } = useLocation();
	const dispatch = useDispatch();

	return (
		<nav
			aria-label="Primary"
			data-hide-on-keyboard
			className="fixed inset-x-0 bottom-0 z-[900] hidden border-t border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 max-[1020px]:block"
			style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
		>
			<ul className="mx-auto flex h-16 max-w-md items-stretch justify-around">
				{TABS.map((tab) => {
					const Icon = tab.icon;
					const active = Array.isArray(tab.match)
						? tab.match.some((m) => pathname.startsWith(m))
						: pathname.startsWith(tab.match);
					return (
						<li key={tab.navId} className="flex-1">
							<NavLink
								to={tab.path}
								onClick={(e) => {
									// Gate auth-only sections (e.g. Orders): if the
									// user isn't logged in, block navigation and open
									// the login popup instead.
									if (tab.requiresAuth && !localStorage.getItem("token")) {
										e.preventDefault();
										dispatch(loginPopupHandler(true));
										return;
									}
									window.scrollTo(0, 0);
									trackNavClick(tab.navId, {
										label: tab.label,
										path: tab.path,
										source: "bottom_nav",
									});
								}}
								className="relative flex h-full flex-col items-center justify-center gap-1 px-1"
							>
								<span className="relative inline-flex">
									<Icon
										className={
											"h-[22px] w-[22px] transition-colors " +
											(active
												? "text-[#153568]"
												: "text-slate-400")
										}
										strokeWidth={active ? 2.4 : 2}
									/>
									{tab.isNew && (
										<span className="absolute -right-1.5 -top-1 h-2 w-2 rounded-full bg-[#f0a500] ring-2 ring-white" />
									)}
								</span>
								<span
									className={
										"max-w-full truncate text-[10px] leading-none transition-colors " +
										(active
											? "font-semibold text-[#153568]"
											: "font-medium text-slate-500")
									}
								>
									{tab.label}
								</span>
								{active && (
									<span className="absolute inset-x-3 top-0 h-0.5 rounded-full bg-[#153568]" />
								)}
							</NavLink>
						</li>
					);
				})}
			</ul>
		</nav>
	);
}
